/**
 * Настройки, которые живут в браузере и на сервер не уходят.
 *
 * Пока это только тема. Отдельно от `AppState`: то — сессия, она
 * переживает перезагрузку через токен и в теории приезжает с сервера;
 * это — вкус конкретного устройства, и на другом устройстве того же
 * человека он вправе отличаться.
 */
import { useCallback, useEffect, useState } from 'react'

const THEME_KEY = 'cardbazar.theme'

/// `system` — не значение темы, а отказ выбирать: тогда решает ОС.
export type ThemeChoice = 'system' | 'light' | 'dark'

export const THEME_TITLES: Record<ThemeChoice, string> = {
  system: 'Как в системе',
  light: 'Светлая',
  dark: 'Тёмная'
}

function isChoice(value: string | null): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark'
}

function readTheme(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    return isChoice(stored) ? stored : 'system'
  } catch {
    // Приватный режим может запрещать storage — работаем без него.
    return 'system'
  }
}

/**
 * Тема применяется атрибутом на `<html>`, а не классом на контейнере:
 * `theme.css` объявляет переменные на `:root[data-theme=…]`, и заодно
 * это красит фон страницы за пределами разметки приложения — резинку
 * прокрутки и подложку под мобильной адресной строкой.
 *
 * При `system` атрибут снимается совсем: тогда работает медиазапрос
 * `prefers-color-scheme`. Поставить `data-theme="system"` было бы
 * ошибкой — под такой селектор нет правил, и тема застряла бы светлой.
 */
function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
}

export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [theme, setTheme] = useState<ThemeChoice>(readTheme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const choose = useCallback((next: ThemeChoice) => {
    setTheme(next)
    try {
      localStorage.setItem(THEME_KEY, next)
    } catch {
      /* см. readTheme */
    }
  }, [])

  return [theme, choose]
}

/**
 * Тема ставится до первого рендера — из `main.tsx`. Иначе страница
 * успевает мигнуть светлым, прежде чем React смонтирует кабинет и
 * применит сохранённый выбор.
 */
export function applyStoredTheme(): void {
  applyTheme(readTheme())
}
